/* eslint-disable no-underscore-dangle */
import { LitElement, TemplateResult, css, html } from 'lit';

import { AllowedFormatElements, Currency, Language, Product } from './types';

const fontHref = 'https://fonts.googleapis.com/css2?family=Poppins:wght@400;600&display=swap';

export class SkipPayBaseElement extends LitElement {
	constructor() {
		super();

		this.createFallbackFont();

		const fontExists = document.head.querySelector('#poppins-font');

		if (fontExists) {
			return;
		}

		const font = document.createElement('link');
		font.href = fontHref;
		font.id = 'poppins-font';
		font.rel = 'stylesheet';
		document.head.appendChild(font);
	}

	private createFallbackFont(): void {
		if (document.head.querySelector('#poppins-fallback')) {
			return;
		}

		const style = document.createElement('style');
		style.innerHTML = `
			@font-face {
				font-family: 'Poppins Fallback';
				font-style: normal;
				font-weight: 400;
				src: local('Arial');
				ascent-override: 105.00%;
				descent-override: 35.00%;
				line-gap-override: 10.00%;
				size-adjust: 100.00%;
			}
		`;
		style.id = 'poppins-fallback';
		document.head.appendChild(style);
	}

	private normalizeMerchantName(merchantName: string): string {
		return merchantName.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
	}

	public getMerchantName(merchantName: string): string {
		const blockedNames = [
			'eshop.cz',
			'example-store.cz',
			'eshop.sk',
			'example-store.sk',
			'eshop.hu',
			'example-store.hu',
		];
		return blockedNames.includes(merchantName)
			? window.location.hostname
			: this.normalizeMerchantName(merchantName);
	}

	public formatAmountForLocale(amount: number, language: Language): string {
		switch (language) {
			case 'sk':
				return amount.toLocaleString('sk-SK').replace(' ', '\u00A0');
			case 'hu':
				return amount.toLocaleString('hu-HU').replace(' ', '\u00A0');
			default:
				return amount.toLocaleString('cs-CZ').replace(' ', '\u00A0');
		}
	}

	public getCurrencySymbol(currency: Currency | undefined): string {
		switch (currency) {
			case 'EUR':
				return '€';
			case 'HUF':
				return 'Ft';
			default:
				return 'Kč';
		}
	}

	public replacePlaceholders(text: string, placeholders: Record<string, string | number>): string {
		return text.replace(/{(.*?)}/g, (_, key) => {
			return placeholders[key] !== undefined ? String(placeholders[key]) : `{${key}}`;
		});
	}

	public calculateInstallments(T: number): [number, number, number] {
		const m = 3;
		const S1 = Math.round(T / m);
		const S2 = Math.round((T - S1) / (m - 1));
		const S3 = T - S1 - S2;
		return [S1, S2, S3].sort((a, b) => a - b) as [number, number, number];
	}

	public checkBannerVisibility(
		product: Product,
		amount: number | undefined,
		currency: Currency | undefined
	): boolean {
		if (product === 'PAY_IN_THREE' && amount && currency) {
			return (
				(currency === 'CZK' && amount >= 1000) ||
				(currency === 'EUR' && amount >= 30) ||
				(currency === 'HUF' && amount >= 11000)
			);
		}
		return true;
	}

	/**
	 * Universal function for formatting text with placeholders and optional HTML styling
	 * @param text - The text with placeholders like "{amount}", "{merchant}", etc.
	 * @param replacements - Object with placeholder keys and their replacement values
	 * @param styledPlaceholders - Object mapping placeholder keys to HTML tag names or array of placeholder keys for <strong> tags
	 * @returns Lit TemplateResult with proper HTML formatting
	 */
	public _formatTextWithPlaceholders(
		text: string,
		replacements: Record<string, string>,
		styledPlaceholders: Record<string, AllowedFormatElements> = {}
	) {
		if (Object.keys(styledPlaceholders).length === 0) {
			let result = text;
			Object.entries(replacements).forEach(([key, value]) => {
				result = result.replace(`{${key}}`, value);
			});
			return html`${result}`;
		}

		let workingText = text;
		const templateParts: Array<string | TemplateResult> = [];
		const remainingReplacements = { ...replacements };

		Object.entries(styledPlaceholders).forEach(([placeholder, tag]) => {
			if (remainingReplacements[placeholder]) {
				const placeholderPattern = `{${placeholder}}`;
				const parts = workingText.split(placeholderPattern);

				if (parts.length === 2) {
					const [firstPart, secondPart] = parts;
					if (firstPart) {
						templateParts.push(firstPart);
					}
					templateParts.push(this._createStyledElement(tag, remainingReplacements[placeholder]));
					workingText = secondPart;
					delete remainingReplacements[placeholder];
				}
			}
		});

		if (workingText) {
			Object.entries(remainingReplacements).forEach(([key, value]) => {
				workingText = workingText.replace(`{${key}}`, value);
			});
			templateParts.push(workingText);
		}

		return html`${templateParts}`;
	}

	/**
	 * Creates a styled HTML element based on the tag name
	 * @param tag - The HTML tag name
	 * @param content - The content to wrap in the tag
	 * @returns TemplateResult with the styled element
	 */
	private _createStyledElement(tag: AllowedFormatElements, content: string): TemplateResult {
		switch (tag.toLowerCase()) {
			case 'strong':
			case 'b':
				return html`<strong>${content}</strong>`;
			case 'em':
			case 'i':
				return html`<em>${content}</em>`;
			case 'u':
				return html`<u>${content}</u>`;
			case 'mark':
				return html`<mark>${content}</mark>`;
			case 'small':
				return html`<small>${content}</small>`;
			case 'code':
				return html`<code>${content}</code>`;
			case 'span':
				return html`<span>${content}</span>`;
			default:
				return html`${content}`;
		}
	}

	static override get styles() {
		return css`
			:host {
				display: block;
				font-family: 'Poppins', 'Poppins Fallback', Arial, sans-serif;
				font-weight: 400;
				box-sizing: border-box;
			}

			a {
				color: #1e82f0;
				text-decoration: underline;
			}
		`;
	}
}
